/**
 * EPI Types for MEV-Med Frontend
 * Type definitions for EPI (Equipamentos de Proteção Individual) management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de EPIs
 */

import { centsToReais } from '../utils/financialHelpers';
import type { DependencyCheck } from './dependencyCheck';

/**
 * Enum for EPI status
 */
export enum EpiStatus {
  ATIVO = 'Sim',
  INATIVO = 'Não'
}

/**
 * Enum for Risk Type - uses backend standard values
 */
export enum TipoDeRisco {
  FISICO = 'FISICO',
  QUIMICO = 'QUIMICO',
  BIOLOGICO = 'BIOLOGICO',
  ERGONOMICO = 'ERGONOMICO',
  ACIDENTE = 'ACIDENTE'
}

/**
 * Enum for EPI Efficiency
 */
export enum EpiEfficiency {
  EFICAZ = 'EFICAZ',
  NAO_EFICAZ = 'NAO_EFICAZ'
}

/**
 * EPI classification (calculated by backend)
 */
export interface EpiClassification {
  tipoDeRisco: TipoDeRisco;
  isActive: boolean;
  isCaExpired: boolean;
  isCaExpiringSoon: boolean;
  daysUntilExpiration: number | null;
}

/**
 * EPI entity interface
 */
export interface Epi {
  id: number;
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string | null;
  ativo: EpiStatus;
  inicioValidade?: string | null;
  fimValidade: string;
  atenuacao?: string | null;
  efficiency?: string | null;
  brandModel?: string | null;
  metodo: string;
  custo?: number | null;
  quantidade?: number | null;
  unidade?: string | null;
  tamanho?: string | null;
  observacao?: string | null;
  orientacaoDeUso?: string | null;
  classification?: EpiClassification;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing EPIs
 */
export interface EpiFilters {
  search?: string;
  tipoDeRisco?: TipoDeRisco;
  ativo?: EpiStatus;
  caExpired?: boolean;
  caExpiringSoon?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating EPIs
 * This represents the UI form state
 */
export interface EpiFormData {
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string;
  ativo: EpiStatus;
  inicioValidade?: string;
  fimValidade: string;
  atenuacao?: string;
  efficiency?: string;
  brandModel?: string;
  metodo: string;
  custo?: number;
  quantidade?: number;
  unidade?: string;
  tamanho?: string;
  observacao?: string;
  orientacaoDeUso?: string;
}

/**
 * API payload for creating/updating EPIs
 * This represents what the backend expects
 */
export interface EpiApiPayload {
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string;
  ativo?: EpiStatus;
  inicioValidade?: string;
  fimValidade: string;
  atenuacao?: string;
  efficiency?: string;
  brandModel?: string;
  metodo: string;
  custo?: number;
  quantidade?: number;
  unidade?: string;
  tamanho?: string;
  observacao?: string;
  orientacaoDeUso?: string | null;
}

/**
 * Response for list endpoint with pagination
 */
export interface EpiListResponse {
  epis: Epi[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Dependency check response
 */
export type EpiDependencies = DependencyCheck;

/**
 * CA Alerts response
 */
export interface EpiCaAlerts {
  expired: Epi[];
  expiringSoon: Epi[];
  expiredCount: number;
  expiringSoonCount: number;
}

/**
 * Props for UI components
 */
export interface EpiTableProps {
  epis: Epi[];
  onEdit: (epi: Epi) => void;
  onDelete: (id: number) => void;
  onView?: (epi: Epi) => void;
  onActivate?: (id: number) => void;
  onDeactivate?: (id: number) => void;
  loading?: boolean;
}

export interface EpiFormProps {
  initialData?: Epi;
  onSubmit: (data: EpiFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface EpiFiltersProps {
  filters: EpiFilters;
  onChange: (filters: EpiFilters) => void;
}

export interface TipoDeRiscoChipProps {
  tipo: TipoDeRisco;
  size?: 'small' | 'medium';
}

export interface CaStatusChipProps {
  fimValidade: string;
  size?: 'small' | 'medium';
}

/**
 * Helper function to get TipoDeRisco display label
 */
export function getTipoDeRiscoLabel(tipo: TipoDeRisco): string {
  const labels: Record<TipoDeRisco, string> = {
    [TipoDeRisco.FISICO]: 'Físico',
    [TipoDeRisco.QUIMICO]: 'Químico',
    [TipoDeRisco.BIOLOGICO]: 'Biológico',
    [TipoDeRisco.ERGONOMICO]: 'Ergonômico',
    [TipoDeRisco.ACIDENTE]: 'Acidente'
  };
  return labels[tipo] || tipo;
}

/**
 * Helper function to get TipoDeRisco color for chips
 */
export function getTipoDeRiscoColor(tipo: TipoDeRisco): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const colors: Record<TipoDeRisco, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    [TipoDeRisco.FISICO]: 'info',
    [TipoDeRisco.QUIMICO]: 'warning',
    [TipoDeRisco.BIOLOGICO]: 'error',
    [TipoDeRisco.ERGONOMICO]: 'success',
    [TipoDeRisco.ACIDENTE]: 'default'
  };
  return colors[tipo] || 'default';
}

/**
 * Helper function to get EpiStatus display label
 */
export function getEpiStatusLabel(status: EpiStatus): string {
  return status === EpiStatus.ATIVO ? 'Ativo' : 'Inativo';
}

/**
 * Helper function to check if CA is expired
 */
export function isCaExpired(fimValidade: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(fimValidade);
  return expirationDate < today;
}

/**
 * Helper function to check if CA is expiring soon (within 30 days)
 */
export function isCaExpiringSoon(fimValidade: string, days: number = 30): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(fimValidade);
  const diffTime = expirationDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= days;
}

/**
 * Helper function to get days until CA expiration
 */
export function getDaysUntilExpiration(fimValidade: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(fimValidade);
  const diffTime = expirationDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Helper function to format cost from cents to currency
 */
export function formatCost(custo: number | null | undefined): string {
  const reais = centsToReais(custo);
  if (reais === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(reais);
}

/**
 * Helper function to get EPI efficiency display label
 */
export function getEfficiencyLabel(efficiency: string | null | undefined): string {
  if (!efficiency) return '-';
  const labels: Record<string, string> = {
    EFICAZ: 'Eficaz',
    NAO_EFICAZ: 'Não Eficaz',
  };
  return labels[efficiency] ?? efficiency;
}
